/**
 * Custom UI Utils
 */
const cuiUtils = {
    getComputed: {
        transitionDuration(element) {
            // transition-duration 값을 숫자로 변환 (ms)
            return parseFloat(window.getComputedStyle(element).transitionDuration) * 1000;
        },
        height(element) {
            return parseFloat(window.getComputedStyle(element).height);
        },
        zIndex(element) {
            return window.getComputedStyle(element).zIndex;
        },
    },

    getTransitionDuration(element) {
        return cuiUtils.getComputed.transitionDuration(element) ?? 0;
    },

    a11yKey(params = {}, event) {
        // 키보드 접근성
        const target = event.target;

        const defaultOptions = {
            type: 'default', // 'trap' or 'default'
            callback: null,
        };
        const options = { ...defaultOptions, ...params };

        const isVisible = (element) => {
            return element.getBoundingClientRect().width > 0 && element.getBoundingClientRect().height > 0;
        };

        const root = event.currentTarget;
        const focusableElements = [
            ...root.querySelectorAll('a, button, input, select, details, textarea, [tabindex]:not([tabindex="-1"])'),
        ].filter(isVisible);
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const keyTab = event.key === 'Tab' || event.keyCode === 9;
        const keyShift = event.shiftKey;
        const isTabPressed = keyTab && !keyShift;
        const isShiftTabPressed = keyShift && keyTab;

        if (options.type === 'trap') {
            // kayboard trap
            if (target === lastElement && isTabPressed) {
                event.preventDefault();
                setTimeout(() => firstElement.focus(), 50);
            } else if (target === firstElement && isShiftTabPressed) {
                event.preventDefault();
                setTimeout(() => lastElement.focus(), 50);
            }
        } else {
            // default
            if (target === lastElement && isTabPressed) {
                if (typeof options.callback === 'function') options.callback();
            } else if (target === firstElement && isShiftTabPressed) {
                if (typeof options.callback === 'function') options.callback();
            }
        }
    },

    openSlide(slideElement, params = {}) {
        let isVisible = false;

        const defaultOptions = {
            activeElement: null,
            activeClass: 'is-active',
            duration: 300, // (ms)
            useAttrHidden: true, // hidden 속성 사용 여부
            on: {
                slideStart: null,
                slideEnd: null
            }
        }
        const options = { ...defaultOptions, ...params };

        slideElement.style.height = 0;
        slideElement.style.transition = `height ${options.duration}ms`;
        slideElement.style.overflow = 'hidden';
        if (options.useAttrHidden) {
            slideElement.hidden = false;
        } else {
            slideElement.style.display = 'block';
        }

        requestAnimationFrame(() => {
            isVisible = true;

            // slideElement.offsetHeight;
            slideElement.style.height = slideElement.scrollHeight + 'px';
            if (options.activeElement) options.activeElement.classList.add(options.activeClass);

            if (typeof options.on.slideStart === 'function') {
                options.on.slideStart();
            }

            setTimeout(() => {
                if (isVisible && (options.activeElement && options.activeElement.classList.contains(options.activeClass))) {
                    // css 초기화 - 반응형 대응
                    slideElement.style.height = '';
                    slideElement.style.transition = '';
                    slideElement.style.overflow = '';

                    if (typeof options.on.slideEnd === 'function') {
                        options.on.slideEnd();
                    }
                }
            }, options.duration);
        });
    },

    closeSlide(slideElement, params = {}) {
        let isVisible = true;

        const defaultOptions = {
            activeElement: null,
            activeClass: 'is-active',
            duration: 300, // (ms)
            useAttrHidden: true, // hidden 속성 사용 여부
            on: {
                slideStart: null,
                slideEnd: null
            }
        }
        const options = { ...defaultOptions, ...params };

        slideElement.style.height = slideElement.scrollHeight + 'px';
        slideElement.style.transition = `height ${options.duration}ms`;
        slideElement.style.overflow = 'hidden';
        if (options.activeElement) options.activeElement.classList.remove(options.activeClass);

        if (typeof options.on.slideStart === 'function') {
            options.on.slideStart();
        }

        requestAnimationFrame(() => {
            slideElement.style.height = 0;

            setTimeout(() => {
                if (isVisible && (options.activeElement && !options.activeElement.classList.contains(options.activeClass))) {
                    if (options.useAttrHidden) {
                        slideElement.hidden = true;
                    } else {
                        slideElement.style.display = 'none';
                    }

                    // css 초기화 - 반응형 대응
                    slideElement.style.height = '';
                    slideElement.style.transition = '';
                    slideElement.style.overflow = '';
                    slideElement.style.display = '';

                    if (typeof options.on.slideEnd === 'function') {
                        options.on.slideEnd();
                    }

                    isVisible = false;
                }
            }, options.duration);
        });
    }
}


/**
 * GNB - PC
 */
class CustomHeader extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            header: 'custom-header',
            navMenus: 'custom-header__nav',
            state: {
                active: 'is-active',
                current: 'is-current'
            },
        };

        this.onCloseBackdropHandle = this.onCloseBackdrop.bind(this);
    }

    get backdrop() {
        return document.getElementById(`${this.classes.header}-backdrop`);
    }

    get sideNav() {
        const getId = this.buttonMobileMenu.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get topSearch() {
        const getId = this.buttonSearch.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    initElements() {
        this.header = this;
        this.navMenus = this.header.querySelector(`.${this.classes.navMenus}`);
        this.buttonMobileMenu = this.header.querySelector('[data-mobile-menu="open"]');
        this.buttonSearch = this.header.querySelector('[data-search-menu="open"]');

        if (!this.backdrop) {
            const createBackdrop = document.createElement('div');
            createBackdrop.id = `${this.classes.header}-backdrop`;
            createBackdrop.classList.add('backdrop');
            document.body.prepend(createBackdrop);
        }
    }

    initEventListeners() {
        this.navMenus.addEventListener('mouseenter', this.onEnterMenu.bind(this), true);
        this.navMenus.addEventListener('focus', this.onEnterMenu.bind(this), true);
        this.navMenus.addEventListener('touchstart', this.onEnterMenu.bind(this));
        this.navMenus.addEventListener('mouseleave', this.onLeaveNavMenus.bind(this));
        this.navMenus.addEventListener('keydown', cuiUtils.a11yKey.bind(this, { callback: () => this.onLeaveNavMenus() }));
        this.header.addEventListener('mouseenter', this.onEnterParent.bind(this));
        this.header.addEventListener('focusin', this.onEnterParent.bind(this));
        this.buttonMobileMenu?.addEventListener('click', this.onDrawSideNav.bind(this));
        this.buttonSearch?.addEventListener('click', this.onDrawSearch.bind(this));
    }

    removeEventListeners() {
        this.removeEventBackdrop();
    }

    removeEventBackdrop() {
        this.backdrop?.removeEventListener('click', this.onCloseBackdropHandle);
    }

    initActiveState() {
        const prev = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.active}`);
        
        if (prev) {
            prev.classList.remove(this.classes.state.active);
            
            const subDepth = prev.querySelector('.nav__subwrap');
            if (subDepth) {
                subDepth.hidden = true;
                this.backdrop?.classList.remove(this.classes.state.active);
            }
        }
    }

    onEnterMenu(e) {
        const target = e.target;

        if (target.closest('.nav__depth1__link')) {
            this.initActiveState();

            const item = target.closest('li');
            item.classList.add(this.classes.state.active);
            
            const subDepth = item.querySelector('.nav__subwrap');
            if (subDepth) {
                e.preventDefault(); // GNB 하위뎁스 노출되기 전에 링크 이동되는 것 방지 - 태블릿 touchstart 고려

                subDepth.hidden = false;
                this.backdrop?.classList.add(this.classes.state.active);
                this.backdrop?.addEventListener('click', this.onCloseBackdropHandle); // backdrop 이벤트 리스너 추가 - search backdrop과 중복 호출 방지

                // 2depth 메뉴 위치값 세팅
                if (!subDepth.classList.contains('nav__subwrap--mega')) {
                    const subDepthMenus = subDepth.querySelector('.nav__depth2');
                    const setPosition = () => {
                        let result;
                        const parentRect = this.navMenus.getBoundingClientRect();
                        const listRect = subDepthMenus.getBoundingClientRect();
                        const itemRect = item.getBoundingClientRect();
                        const left = parentRect.left;
                        const right = parentRect.right - listRect.width;
                        const center = (itemRect.width <= listRect.width) ? itemRect.left - (listRect.width - itemRect.width) / 2 : itemRect.left;
    
                        if (parentRect.right <= center + listRect.width) {
                            result = right;
                        } else if (parentRect.left >= center) {
                            result = left;
                        } else {
                            result = center;
                        }
    
                        return result;
                    }
    
                    subDepthMenus.style.transform = `translateX(${setPosition()}px)`;
                }
            } else {
                this.backdrop?.classList.remove(this.classes.state.active);
            }
        }
    }

    onEnterParent() {
        if (this.header.classList.contains(this.classes.state.active)) return;
        this.header.classList.add(this.classes.state.active);

        this.header.addEventListener('mouseleave', () => {
            this.onLeaveParent();
        });

        this.header.addEventListener('focusout', () => {
            setTimeout(() => {
                const focusedElement = document.activeElement;
                if (focusedElement !== document.body && !this.header.contains(focusedElement)) {
                    this.onLeaveParent();
                }
            }, 50);
        });
    }

    onLeaveParent() {
        if (this.topSearch.hidden === false || this.sideNav.hidden == false) return;
        this.header.classList.remove(this.classes.state.active);
    }

    onLeaveNavMenus() {
        this.initActiveState();
    }

    onCloseBackdrop() {
        this.onLeaveNavMenus();
        this.removeEventBackdrop(); // backdrop 이벤트 리스너 제거 - search backdrop과 중복 호출 방지
    }

    onDrawSideNav() {
        // 모바일 메뉴
        this.sideNav.hidden = false;
        this.buttonMobileMenu.setAttribute('aria-expanded', true);
        this.header.classList.add(this.classes.state.active);
        document.documentElement.classList.add('custom-scroll-lock');
        
        const buttonClose = this.sideNav.querySelector('[data-mobile-menu="close"]');
        setTimeout(() => {
            this.sideNav.classList.add('is-open');
            this.sideNav.querySelector('.custom-sidenav__wrap').addEventListener('keydown', (event) => {
                cuiUtils.a11yKey({ type: 'trap' }, event); // 키보드 접근성
            });
            if (buttonClose) buttonClose.focus();
        }, 10);
    }

    onDrawSearch() {
        // 상단 검색바
        if (this.buttonSearch.classList.contains(this.classes.state.active)) {
            this.onCloseSearch();
        } else {
            if (this.backdrop) {
                this.backdrop.style.zIndex = cuiUtils.getComputed.zIndex(this.topSearch);
                this.backdrop.classList.add(this.classes.state.active);
            }
            
            const buttonClose = this.topSearch.querySelector('[data-search-menu="close"]');
            const duration = cuiUtils.getTransitionDuration(this.topSearch);
            cuiUtils.openSlide(this.topSearch, {
                activeElement: this.buttonSearch,
                duration: duration,
                on: {
                    slideStart: () => {
                        this.header.classList.add(this.classes.state.active);
                        document.documentElement.classList.add('custom-scroll-lock');
                        this.topSearch.classList.add('is-open');
                        this.buttonSearch.setAttribute('aria-expanded', true);
                    },
                    slideEnd: () => {
                        this.topSearch.addEventListener('keydown', (event) => {
                            cuiUtils.a11yKey({
                                type: 'default',
                                callback: () => {
                                    this.onCloseSearch();
                                }
                            }, event); // 키보드 접근성
                        });
                        if (buttonClose) buttonClose.focus();
                    }
                }
            });
        }
    }

    onCloseSearch() {
        this.topSearch.onCloseSearch();
    }
}
customElements.define('custom-header', CustomHeader);


/**
 * GNB - Mobile
 */
class CustomMobileSideNav extends HTMLElement {
    constructor() {
        super();

        this.attachShadow({ mode: 'open' });
        this.shadowRoot.innerHTML = `
            <slot></slot>
            <div part="backdrop"></div>
        `;

        this.classes = {
            sideNav: 'custom-sidenav',
            navMenus: 'custom-sidenav__nav',
            header: 'custom-header',
            state: {
                active: 'is-active',
                current: 'is-current'
            },
        };
    }

    get backdrop() {
        return this.shadowRoot.querySelector('[part="backdrop"]');
    }

    get languagePopup() {
        const getId = this.ButtonLang.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onMediaQuery();
    }

    initElements() {
        this.sideNav = this;
        this.ButtonClose = this.sideNav.querySelector('[data-mobile-menu="close"]');
        this.ButtonLang = this.sideNav.querySelector('[data-mobile-language="open"]');
        this.navMenus = this.sideNav.querySelector(`.${this.classes.navMenus}`);
        this.header = document.querySelector(`.${this.classes.header}`);

        this.setCurrentMenu();
    }

    initEventListeners() {
        this.navMenus.addEventListener('click', this.onEnterMenu.bind(this), true);
        this.backdrop?.addEventListener('click', this.onCloseNav.bind(this));
        this.ButtonClose?.addEventListener('click', this.onCloseNav.bind(this));
        this.ButtonLang?.addEventListener('click', this.onDrawLanguage.bind(this));
    }

    initActiveState() {
        this.clearActiveState(1);
        this.clearActiveState(2);
    }

    clearActiveState(depth) {
        let prevItem;

        switch (depth) {
            case 1:
                prevItem = this.navMenus.querySelector(`.nav__depth1 >li.${this.classes.state.active}`);
                break;
            case 2:
                prevItem = this.navMenus.querySelector(`.nav__depth2 >li.${this.classes.state.active}`);
                break;
            default:
                break;
        }

        if (prevItem) {
            const subDepth = prevItem.querySelector('.nav__subwrap');
            if (subDepth) {
                this.closeMenu(prevItem, subDepth);
            }
        }
    }

    setCurrentMenu() {
        const currentItems = this.sideNav.querySelectorAll(`li.${this.classes.state.current}`);
        currentItems.forEach((item) => {
            const subDepth = item.querySelector('.nav__subwrap');
            if (subDepth) {
                item.classList.add(this.classes.state.active);
                this.openMenu(item, subDepth);
            }
        });
        
    }

    openMenu(item, subDepth) {
        const duration = cuiUtils.getTransitionDuration(subDepth);
        cuiUtils.openSlide(subDepth, {
            activeElement: item,
            duration: duration
        });
    }
    
    closeMenu(item, subDepth) {
        const duration = cuiUtils.getTransitionDuration(subDepth);
        cuiUtils.closeSlide(subDepth, {
            activeElement: item,
            duration: duration
        });
    }

    onEnterMenu(e) {
        const target = e.target;

        const collapse = (target, subWrapSelector, callback) => {
            const item = target.closest('li');
            const subDepth = item.querySelector(subWrapSelector);

            if (subDepth) {
                e.preventDefault();

                if (item.classList.contains(this.classes.state.active)) {
                    this.closeMenu(item, subDepth);
                } else {
                    if (typeof callback === 'function') callback();
                    this.openMenu(item, subDepth);
                }
            }
        };

        if (target.closest('.nav__depth1__link')) {
            // 1depth
            collapse(target, '.nav__subwrap', () => {
                this.clearActiveState(1);
                this.clearActiveState(2);
            });
        } else if (target.closest('.nav__depth2__link')) {
            // 2depth
            collapse(target, '.nav__subwrap', () => {
                this.clearActiveState(2);
            });
        }
    }

    onCloseNav() {
        if (this.sideNav.hidden === true) return;

        const headerbutton = this.header.querySelector('[data-mobile-menu="open"]');
        const sideNavInner = this.sideNav.querySelector(`.${this.classes.sideNav}__wrap`);
        const duration = cuiUtils.getTransitionDuration(sideNavInner);
        
        this.onCloseLanguage();
        this.sideNav.classList.remove('is-open');
        document.documentElement.classList.remove('custom-scroll-lock');
        setTimeout(() => {
            this.sideNav.hidden = true;
            this.initActiveState();
            this.setCurrentMenu();
            headerbutton.setAttribute('aria-expanded', false);
            headerbutton.focus();
        }, duration);
    }

    onDrawLanguage() {
        this.ButtonLang.setAttribute('aria-expanded', true);
        this.languagePopup.classList.add('is-open');
        this.languagePopup.addEventListener('keydown', (event) => {
            cuiUtils.a11yKey({ type: 'trap' }, event); // 키보드 접근성
        });
        
        const buttonClose = this.languagePopup.querySelector('[data-mobile-language="close"]');
        if (buttonClose) {
            buttonClose.focus();
            buttonClose.addEventListener('click', this.onCloseLanguage.bind(this));
        }
    }

    onCloseLanguage() {
        if (!this.languagePopup && !this.languagePopup.classList.contains('is-open')) return;

        this.languagePopup.classList.remove('is-open');
        this.ButtonLang.setAttribute('aria-expanded', false);
        this.ButtonLang.focus();
    }

    onMediaQuery() {
        const mm = {
            desktop: window.matchMedia('(min-width: 1024px)'),
        };

        const updateMatches = () => {
            if (mm.desktop.matches) {
                this.onCloseNav();
            }
        };

        updateMatches();
        mm.desktop.addEventListener('change', updateMatches);
    }
}
customElements.define('custom-mobile-sidenav', CustomMobileSideNav);


/**
 * Top Search
 */
class CustomTopSearch extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            topSearch: 'custom-top-search',
            inputSearch: 'custom-top-search__input--text',
            header: 'custom-header',
            state: {
                active: 'is-active',
            },
        };

        this.onCloseBackdropHandle = this.onCloseBackdrop.bind(this);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
    }

    disconnectedCallback() {
        this.removeEventBackdrop();
    }

    initElements() {
        this.topSearch = this;
        this.buttonClose = this.topSearch.querySelector('[data-search-menu="close"]');
        this.inputSearch = this.topSearch.querySelector(`.${this.classes.inputSearch}`);
        this.header = document.querySelector(`.${this.classes.header}`);
        this.backdrop = document.getElementById(`${this.classes.header}-backdrop`);

        this.onChangeValue();
    }

    initEventListeners() {
        this.buttonClose?.addEventListener('click', this.onCloseSearch.bind(this));
        this.inputSearch?.addEventListener('input', this.onChangeValue.bind(this));
        this.backdrop?.addEventListener('click', this.onCloseBackdropHandle);
    }

    removeEventBackdrop() {
        this.backdrop?.removeEventListener('click', this.onCloseBackdropHandle);
    }

    onChangeValue() {
        const button = this.topSearch.querySelector('.custom-top-search__button--clear');
        button.addEventListener('click', () => {
            button.style.display = '';
            this.inputSearch.value = '';
            this.inputSearch.focus();
        });

        if (this.inputSearch.value !== '') {
            button.style.display = 'block';
        } else {
            button.style.display = '';
        }
    }

    onCloseSearch() {
        if (this.topSearch.hidden === true) return;

        const headerbutton = this.header.querySelector('[data-search-menu="open"]');
        const duration = cuiUtils.getTransitionDuration(this.topSearch);
        cuiUtils.closeSlide(this.topSearch, {
            activeElement: headerbutton,
            duration: duration,
            on: {
                slideStart: () => {
                    this.topSearch.classList.remove('is-open');
                    document.documentElement.classList.remove('custom-scroll-lock');

                    if (this.backdrop) {
                        this.backdrop.classList.remove(this.classes.state.active);
                        this.backdrop.style = '';
                    }
                },
                slideEnd: () => {
                    headerbutton.setAttribute('aria-expanded', false);
                    headerbutton.focus();
                }
            }
        });
    }

    onCloseBackdrop() {
        this.onCloseSearch();
    }
}
customElements.define('custom-top-search', CustomTopSearch);


/**
 * Footer
 */
class CustomFooter extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            footer: 'custom-footer',
            goTopButton: 'custom-footer-gotop',
            state: {
                active: 'is-active',
            },
        };
    }

    get footerBody() {
        const getId = this.toggleButton.getAttribute('aria-controls');
        return document.getElementById(getId);
    }

    get goTopButton() {
        return document.querySelector(`.${this.classes.goTopButton}`);
    }

    connectedCallback() {
        this.initElements();
        this.initEventListeners();
        this.onScroll();
        this.onMediaQuery();
    }

    disconnectedCallback() {
        this.removeEventListeners();
    }

    initElements() {
        this.footer = this;
        this.toggleButton = this.footer.querySelector('[data-footer-menu="toggle"]');
    }

    initEventListeners() {
        this.toggleButton?.addEventListener('click', this.onToggleFnb.bind(this));
        this.goTopButton?.addEventListener('click', this.onPageTop.bind(this));
        window.addEventListener('scroll', this.onScroll.bind(this));
    }

    removeEventListeners() {
        window.removeEventListener('scroll', this.onScroll.bind(this));
    }

    openFnb() {
        cuiUtils.openSlide(this.footerBody, {
            activeElement: this.toggleButton,
            duration: 400,
            useAttrHidden: false,
            on: {
                slideStart: () => {
                    this.toggleButton.setAttribute('aria-expanded', true);
                },
                slideEnd: () => {
                    window.scrollTo({
                        top: document.documentElement.scrollHeight - window.innerHeight,
                        behavior: 'smooth',
                    });
                }
            }
        });
    }

    closeFnb() {
        cuiUtils.closeSlide(this.footerBody, {
            activeElement: this.toggleButton,
            duration: 400,
            useAttrHidden: false,
            on: {
                slideStart: () => {
                    this.toggleButton.setAttribute('aria-expanded', false);
                }
            }
        });
    }

    onToggleFnb() {
        if (this.footerBody) {
            if (this.toggleButton.classList.contains(this.classes.state.active)) {
                this.closeFnb();
            } else {
                this.openFnb();
            }
        }
    }

    initToggleState() {
        if (this.footerBody) {
            this.toggleButton.classList.remove(this.classes.state.active);
            this.toggleButton.setAttribute('aria-expanded', false);
            this.footerBody.style.display = '';
            this.footerBody.style.height = '';
        }
    }

    onPageTop(e) {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onScroll() {
        if (!this.goTopButton) return;

        requestAnimationFrame(() => {
            const winH = window.innerHeight;
            const scrY = window.scrollY;
            const calcDocumentH = document.documentElement.scrollHeight - winH;
            const startY = (winH / 2);
            const offsetY = Math.ceil(scrY + winH);
            const footerStartY = this.footer.offsetTop;

            const triggerY = (startY > calcDocumentH) ? Math.abs(startY - (calcDocumentH + this.footer.offsetHeight)) : startY;

            if (scrY > triggerY) {
                this.goTopButton.classList.add(this.classes.state.active);
                if (offsetY >= footerStartY) {
                    this.goTopButton.classList.add('is-unfixed');
                } else {
                    this.goTopButton.classList.remove('is-unfixed');
                }
            } else {
                this.goTopButton.classList.remove(this.classes.state.active);
            }
        });
    }

    onMediaQuery() {
        const mm = {
            desktop: window.matchMedia('(min-width: 1024px)')
        };

        const updateMatches = () => {
            if (mm.desktop.matches) {
                this.initToggleState();
            }
        };

        updateMatches();
        mm.desktop.addEventListener('change', updateMatches);
    }
}
customElements.define('custom-footer', CustomFooter);


/**
 * Dropdown List
 */
class CustomUIDropdown extends HTMLElement {
    constructor() {
        super();

        this.classes = {
            button: 'custom-ui-dropdown__button',
            options: 'custom-ui-dropdown__options',
            state: {
                active: 'is-active',
                open: 'is-open',
                reverse: 'is-reverse'
            },
        };
    }

    get slideMode() {
        return this.getAttribute('slide');
    }

    get selectMode() {
        return this.hasAttribute('select');
    }

    connectedCallback() {
        this.button = this.querySelector(`.${this.classes.button}`);
        this.options = this.querySelector(`.${this.classes.options}`);
        this.setValue();

        document.addEventListener('mousedown', this.onLeave.bind(this));
        this.addEventListener('click', this.onEnter.bind(this));
    }

    disconnectedCallback() {
        document.removeEventListener('mousedown', this.onLeave.bind(this));
    }

    setTranslateY() {
        let translateY;

        if (this.slideMode === 'up') {
            translateY = 'translateY(100%)';
        } else if (this.slideMode === 'down') {
            translateY = 'translateY(-100%)';
        }
        this.options.style.transition = 'transform .4s';
        this.options.style.transform = translateY;
    }

    open(a11y) {
        const isClippedByViewport = () => {
            // 레이어 하단이 viewport에서 짤리는지 체크
            return this.options.getBoundingClientRect().bottom >= window.innerHeight;
        };

        const onOpenEnd = () => {
            this.classList.add(this.classes.state.open);
            this.options.offsetHeight;
            requestAnimationFrame(() => {
                if (this.slideMode) {
                    this.options.style.transform = 'translateY(0)';
                }

                if (a11y) {
                    const prev = this.options.querySelector(`li.${this.classes.state.active}`) ?? this.options.querySelector(`li:first-child`);
                    if (prev) prev.querySelector('a, input').focus(); // 선택된 항목으로 포커스

                    this.options.addEventListener('keydown', (event) => {
                        const items = Array.from(this.options.querySelectorAll('li'));
                        const focusedElement = event.target.closest('li');
                        const currentIndex = focusedElement ? items.indexOf(focusedElement) : -1;

                        const focusPrevItem = () => {
                            event.preventDefault();
                            if (currentIndex > 0) {
                                const prevItem = items[currentIndex - 1];
                                const prevFocusElement = prevItem.querySelector('a, input');
                                prevFocusElement?.focus();
                            }
                        }

                        const focusNextItem = () => {
                            event.preventDefault();
                            if (currentIndex < items.length - 1) {
                                const nextItem = items[currentIndex + 1];
                                const nextFocusElement = nextItem.querySelector('a, input');
                                nextFocusElement?.focus();
                            }
                        }

                        switch (event.key) {
                            case 'Escape':
                                event.preventDefault();
                                this.close(true);
                                break;
                                
                            case 'ArrowDown':
                                focusNextItem();
                                break;
                                
                            case 'ArrowUp':
                                focusPrevItem();
                                break;
                                
                            case 'Tab':
                                const lastItem = items[items.length - 1];
                                const firstItem = items[0];

                                if (event.shiftKey) {
                                    focusPrevItem();
                                    if (focusedElement === firstItem) this.close(true);
                                    
                                } else if (!event.shiftKey) {
                                    focusNextItem();
                                    if (focusedElement === lastItem) this.close(true);
                                }
                                break;

                            case 'Enter':
                                if (focusedElement && focusedElement.querySelector('input')) {
                                    event.preventDefault();
                                    this.onEnter(event);
                                }
                                break;
                                
                            default:
                                break;
                        }
                    });
                }
            });
        };

        if (this.slideMode) {
            this.setTranslateY();
            setTimeout(() => {
                onOpenEnd();
            }, 50);
        } else {
            onOpenEnd();
        }

        if (isClippedByViewport()) {
            this.classList.add(this.classes.state.reverse);
            this.options.style.bottom = cuiUtils.getComputed.height(this.button) + 'px';
        }
    }

    close(a11y) {
        const onCloseEnd = () => {
            this.classList.remove(this.classes.state.open, this.classes.state.reverse);
            this.options.style.bottom = '';
            this.options.style.transform = '';
            this.options.style.transition = '';

            if (a11y) {
                this.button.focus();
            }
        };

        if (this.slideMode) {
            this.setTranslateY();

            const duration = cuiUtils.getTransitionDuration(this.options);
            setTimeout(() => {
                onCloseEnd();
            }, duration);
        } else {
            onCloseEnd();
        }
    }

    onEnter(e) {
        const target = e.target;

        if (target.closest(`.${this.classes.button}`)) {
            if (this.classList.contains(this.classes.state.open)) {
                this.close(true);
            } else {
                this.open(true);
            }
        }

        if (this.selectMode) {
            const optionItem = target.closest(`.${this.classes.options} li`);
            if (optionItem && !optionItem.classList.contains(this.classes.state.active)) {
                const prev = this.options.querySelector(`li.${this.classes.state.active}`);
                if (prev) {
                    prev.classList.remove(this.classes.state.active);
                    const prevInput = prev.querySelector('input');
                    if (prevInput) prevInput.checked = false;
                }
    
                optionItem.classList.add(this.classes.state.active);
                this.setValue(optionItem);
                this.close(true);

                // 커스텀 이벤트 추가
                this.dispatchChangeEvent(optionItem);
            }
        }
    }

    onLeave(e) {
        const target = e.target;
        if (e.button === 0) {
            // 바닥 페이지 왼클릭시 닫히게
            if (!this.contains(target) && this.classList.contains(this.classes.state.open)) {
                this.close(false);
            }
        }
    }

    setValue(item) { // select mode 이고 선택된 옵션값이 있을때(.is-active), 해당 옵션값으로 버튼명 변경
        const selected = item ? item : this.options.querySelector(`li.${this.classes.state.active}`);
        if (selected) {
            this.button.textContent = selected.textContent;
            const input = selected.querySelector('input');
            if (input) input.checked = true;
        }
    }

    dispatchChangeEvent(item) {
        const customEvent = new CustomEvent('custom-ui-dropdown:change', {
            detail: {
                index: Array.from(this.options.querySelectorAll('li')).indexOf(item),
                element: item.querySelector('a, input'),
                parentElement: item
            },
            bubbles: false
        });

        this.dispatchEvent(customEvent);
    }
}
customElements.define('custom-ui-dropdown', CustomUIDropdown);


/**
 * 공통 컴포넌트
 */
const commonUI = {
    // 관련 제품 컴포넌트 (luxuryset > Customers Also Love)
    relatedProducts(rootEl = document) {
        const productLists = rootEl.querySelectorAll('.component-related-products__list');

        productLists.forEach(list => {
            $(list).slick({
                arrows: true,
                dots: false,
                slidesToShow: 4,
                infinite: false,
                adaptHeight: true,
                responsive: [{
                    breakpoint: 767,
                    settings: {
                        arrows: false,
                        slidesToShow: 3,
                        dots: true
                    }
                }, {
                    breakpoint: 490,
                    settings: {
                        arrows: false,
                        slidesToShow: 2,
                        dots: true
                    }
                }, {
                    breakpoint: 375,
                    settings: {
                        arrows: false,
                        slidesToShow: 1,
                        centerMode: true,
                        infinite: false,
                        adaptHeight: true,
                        variableWidth: true,
                        dots: true
                    }
                }]
            });
        });
    }
};


/**
 * 메인 페이지용 컴포넌트
 */
const pageMain = {
    setKeyVisualHeader(rootEl = document) {
        const keyVisuals = rootEl.querySelectorAll('.renew2022-kv-section');
        const header = document.querySelector('.custom-header');

        keyVisuals.forEach(section => {
	        if (!section.closest('.shopify-section').previousElementSibling) { // 첫번째 요소일때
                header.classList.add('bg-transparent');
            } else {
                header.classList.remove('bg-transparent');
            }
        });
    }
}